const FALLBACK_ZERO = 0;

/** Timeout for weekly-recap API calls (player bundle can be slow). */
export const WEEKLY_RECAP_REQUEST_TIMEOUT_MS = 50_000;

export const WEEKLY_RECAP_MIN_OFFSET = -7;
export const WEEKLY_RECAP_MAX_OFFSET = 7;

export const MD_POSITION_START = "start"; // MD is the FIRST column (tracks recovery after match)
export const MD_POSITION_END = "end";     // MD is the LAST column (tracks preparation before match)

export function getMdPositionOffsets(mdPosition) {
  return mdPosition === MD_POSITION_END
    ? { start_offset: -6, end_offset: 0 }
    : { start_offset: 0, end_offset: 6 };
}

export function offsetToJDayLabel(offset) {
  const n = Number(offset);
  if (!Number.isFinite(n)) return "MD";
  if (n === 0) return "MD";
  return n > 0 ? `MD+${n}` : `MD${n}`;
}

export function jDayLabelToOffset(label) {
  if (typeof label !== "string") return null;
  const txt = label.trim().toUpperCase();
  for (const prefix of ["MD", "J"]) {
    if (txt === prefix) return 0;
    if (!txt.startsWith(prefix)) continue;

    const suffixRaw = txt.slice(prefix.length);
    const suffix = suffixRaw.startsWith("+") ? suffixRaw.slice(1) : suffixRaw;
    const parsed = Number.parseInt(suffix, 10);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

function offsetToLegacyJDayLabel(offset) {
  const n = Number(offset);
  if (!Number.isFinite(n)) return "J";
  if (n === 0) return "J";
  return n > 0 ? `J+${n}` : `J${n}`;
}

export function buildJDaysFromRange(startOffset = -2, endOffset = 5) {
  const min = Math.max(WEEKLY_RECAP_MIN_OFFSET, Math.min(WEEKLY_RECAP_MAX_OFFSET, Number(startOffset) || 0));
  const max = Math.max(WEEKLY_RECAP_MIN_OFFSET, Math.min(WEEKLY_RECAP_MAX_OFFSET, Number(endOffset) || 0));
  const start = Math.min(min, max);
  const end = Math.max(min, max);

  const days = [];
  for (let offset = start; offset <= end; offset += 1) {
    days.push(offsetToJDayLabel(offset));
  }
  return days;
}

export function normalizeMicrocycleConfig(rawConfig, fallback = { md_position: MD_POSITION_START, calculate_similarity_from: "MD+1" }) {
  const cfg = rawConfig && typeof rawConfig === "object" ? rawConfig : {};

  let mdPosition;
  if (cfg.md_position === MD_POSITION_END) {
    mdPosition = MD_POSITION_END;
  } else if (cfg.md_position === MD_POSITION_START) {
    mdPosition = MD_POSITION_START;
  } else {
    // Legacy config: infer from stored offsets (end_offset <= 0 → "end" mode)
    const legacyEnd = Number.parseInt(cfg.end_offset, 10);
    const legacyStart = Number.parseInt(cfg.start_offset, 10);
    if (Number.isFinite(legacyEnd) && Number.isFinite(legacyStart) && legacyEnd <= 0 && legacyStart < 0) {
      mdPosition = MD_POSITION_END;
    } else {
      mdPosition = fallback.md_position === MD_POSITION_END ? MD_POSITION_END : MD_POSITION_START;
    }
  }

  const { start_offset, end_offset } = getMdPositionOffsets(mdPosition);

  const calcDefault = mdPosition === MD_POSITION_END ? "MD-5" : "MD+1";
  const calcRaw = cfg.calculate_similarity_from || fallback.calculate_similarity_from || calcDefault;
  let calcOffset = jDayLabelToOffset(calcRaw);
  if (!Number.isFinite(calcOffset)) calcOffset = mdPosition === MD_POSITION_END ? -5 : 1;
  calcOffset = Math.max(start_offset, Math.min(end_offset, calcOffset));

  return {
    md_position: mdPosition,
    start_offset,
    end_offset,
    calculate_similarity_from: offsetToJDayLabel(calcOffset),
  };
}

export const WEEKLY_RECAP_J_DAYS = buildJDaysFromRange(0, 6);

export const WEEKLY_RECAP_DEFAULT_DAY_PERCENTAGES = Object.freeze({
  "MD-7": 45,
  "MD-6": 55,
  "MD-5": 65,
  "MD-4": 80,
  "MD-3": 90,
  "MD-2": 70,
  "MD-1": 45,
  "MD": 35,
  "MD+1": 35,
  "MD+2": 60,
  "MD+3": 90,
  "MD+4": 65,
  "MD+5": 40,
  "MD+6": 30,
  "MD+7": 25,
});

export const WEEKLY_RECAP_DAY_TONES = Object.freeze([
  {
    // MD+1: Recovery
    lightBadge: "bg-sky-100/80 text-sky-800 border border-sky-200/50 dark:border-none",
    darkBadge: "bg-sky-500/10 text-sky-300 border border-sky-500/10",
    lightCell: "bg-sky-50/15 dark:bg-sky-950/10",
    darkCell: "bg-sky-950/10",
  },
  {
    // MD+2: Restoration
    lightBadge: "bg-emerald-100/80 text-emerald-800 border border-emerald-200/50 dark:border-none",
    darkBadge: "bg-emerald-500/10 text-emerald-300 border border-emerald-500/10",
    lightCell: "bg-emerald-50/15 dark:bg-emerald-950/10",
    darkCell: "bg-emerald-950/10",
  },
  {
    // MD+3: Strength Peak
    lightBadge: "bg-indigo-100/80 text-indigo-800 border border-indigo-200/50 dark:border-none",
    darkBadge: "bg-indigo-500/10 text-indigo-300 border border-indigo-500/10",
    lightCell: "bg-indigo-50/15 dark:bg-indigo-950/10",
    darkCell: "bg-indigo-950/10",
  },
  {
    // MD+4: Speed Peak / Tactical
    lightBadge: "bg-violet-100/80 text-violet-800 border border-violet-200/50 dark:border-none",
    darkBadge: "bg-violet-500/10 text-violet-300 border border-violet-500/10",
    lightCell: "bg-violet-50/15 dark:bg-violet-950/15",
    darkCell: "bg-violet-950/10",
  },
  {
    // MD+5: Pre-match activation
    lightBadge: "bg-amber-100/80 text-amber-800 border border-amber-200/50 dark:border-none",
    darkBadge: "bg-amber-500/10 text-amber-300 border border-amber-500/10",
    lightCell: "bg-amber-50/15 dark:bg-amber-950/15",
    darkCell: "bg-amber-950/10",
  },
]);

// Add or remove metrics here to customize the recap tab reference blocks.
export const WEEKLY_RECAP_REFERENCE_METRICS = [
  {
    key: "global_distance",
    labelKey: "weekly_recap.metrics.global_distance",
    label: "Total distance",
    unit: "km",
    decimals: 2,
    getValue: (player) => Number(player?.global_distance),
  },
  {
    key: "hsr_distance_m",
    labelKey: "weekly_recap.metrics.hsr_distance_m",
    label: "HSR distance",
    unit: "m",
    decimals: 0,
    getValue: (player) => Number(player?.hsr_distance_m),
  },
  {
    key: "spr_distance_m",
    labelKey: "weekly_recap.metrics.spr_distance_m",
    label: "Sprint distance",
    unit: "m",
    decimals: 0,
    getValue: (player) => Number(player?.spr_distance_m),
  },
  {
    key: "sprint_count",
    labelKey: "weekly_recap.metrics.sprint_count",
    label: "Sprint count",
    unit: "",
    decimals: 0,
    getValue: (player) => Number(player?.sprint_count),
  },
  {
    key: "sprint_max",
    labelKey: "weekly_recap.metrics.sprint_max",
    label: "Max sprint speed",
    unit: "km/h",
    decimals: 1,
    getValue: (player) => Number(player?.sprint_max),
  },
  {
    key: "vmax",
    labelKey: "weekly_recap.metrics.vmax",
    label: "Vmax",
    unit: "km/h",
    decimals: 1,
    getValue: (player) => Number(player?.vmax ?? player?.vma ?? player?.sprint_max),
  },
  {
    key: "COD_number",
    labelKey: "weekly_recap.metrics.COD_number",
    label: "COD count",
    unit: "",
    decimals: 0,
    getValue: (player) => Number(player?.COD_number),
  },
  {
    key: "acceleration_max",
    labelKey: "weekly_recap.metrics.acceleration_max",
    label: "Max acceleration",
    unit: "m/s2",
    decimals: 2,
    getValue: (player) => Number(player?.acceleration_max),
  },
  {
    key: "deceleration_max",
    labelKey: "weekly_recap.metrics.deceleration_max",
    label: "Max deceleration",
    unit: "m/s2",
    decimals: 2,
    getValue: (player) => Number(player?.deceleration_max),
  },
];

export const WEEKLY_RECAP_INTERNAL_METRICS = [
  {
    key: "hr",
    labelKey: "weekly_recap.metrics.hr",
    label: "Heart rate",
    unit: "bpm",
    decimals: 0,
    getValue: (player) => Number(player?.max_heart_rate ?? player?.average_heart_rate ?? player?.hr),
  },
  {
    key: "mechanical_fatigue",
    labelKey: "weekly_recap.metrics.mechanical_fatigue",
    label: "Mechanical Fatigue",
    unit: "/10",
    decimals: 1,
    getValue: (player) => Number(player?.mechanical_fatigue),
  },
  {
    key: "intensity",
    labelKey: "weekly_recap.metrics.intensity",
    label: "Intensity",
    unit: "PT",
    decimals: 1,
    getValue: (player) => Number(player?.intensity ?? player?.intensity_index),
  },
  {
    key: "mechanical_load",
    labelKey: "weekly_recap.metrics.mechanical_load",
    label: "Mechanical load",
    unit: "PT",
    decimals: 1,
    getValue: (player) => Number(player?.mechanical_load ?? player?.mechanical_load_index),
  },
];

export const WEEKLY_RECAP_PEAK_SPEED_METRIC_KEYS = ["vmax", "sprint_max"];

export function isWeeklyRecapPeakSpeedMetric(key) {
  return WEEKLY_RECAP_PEAK_SPEED_METRIC_KEYS.includes(key);
}

export function filterWeeklyRecapLoadMetrics(metrics) {
  return (metrics || []).filter((metric) => !isWeeklyRecapPeakSpeedMetric(metric?.key));
}

export function getWeeklyRecapPeakSpeedMetrics(
  metrics = WEEKLY_RECAP_REFERENCE_METRICS,
) {
  const byKey = Object.fromEntries((metrics || []).map((metric) => [metric.key, metric]));
  return WEEKLY_RECAP_PEAK_SPEED_METRIC_KEYS
    .map((key) => byKey[key])
    .filter(Boolean);
}

export const WEEKLY_RECAP_LOAD_METRICS = filterWeeklyRecapLoadMetrics(
  WEEKLY_RECAP_REFERENCE_METRICS,
);

function sanitizeMetricLabel(value) {
  if (typeof value !== "string") return "";
  const trimmed = value.trim();
  if (!trimmed) return "";
  return trimmed.slice(0, 64);
}

export function getDefaultMetricLabel(metric, t) {
  if (!metric) return "";

  if (typeof t === "function" && metric.labelKey) {
    return t(metric.labelKey, { defaultValue: metric.label || metric.key || "" });
  }

  return metric.label || metric.key || "";
}

export function normalizeMetricLabelOverrides(
  rawOverrides,
  metrics = [...WEEKLY_RECAP_REFERENCE_METRICS, ...WEEKLY_RECAP_INTERNAL_METRICS],
) {
  const source = rawOverrides && typeof rawOverrides === "object" && !Array.isArray(rawOverrides)
    ? rawOverrides
    : {};

  const allowed = new Set((metrics || []).map((metric) => metric?.key).filter(Boolean));
  const normalized = {};

  Object.entries(source).forEach(([metricKey, rawLabel]) => {
    if (!allowed.has(metricKey)) return;
    const sanitized = sanitizeMetricLabel(rawLabel);
    if (!sanitized) return;
    normalized[metricKey] = sanitized;
  });

  return normalized;
}

export function getMetricDisplayLabel(metric, t, metricLabelOverrides = {}) {
  const metricKey = metric?.key;
  const override = metricKey ? sanitizeMetricLabel(metricLabelOverrides?.[metricKey]) : "";
  if (override) return override;
  return getDefaultMetricLabel(metric, t);
}

export function toFiniteNumber(value, fallback = FALLBACK_ZERO) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

export function createDefaultPercentageMatrix(
  metrics = WEEKLY_RECAP_REFERENCE_METRICS,
  jDays = WEEKLY_RECAP_J_DAYS,
  dayDefaults = WEEKLY_RECAP_DEFAULT_DAY_PERCENTAGES,
) {
  return Object.fromEntries(
    metrics.map((metric) => [
      metric.key,
      Object.fromEntries(
        jDays.map((day) => [day, toFiniteNumber(dayDefaults?.[day], FALLBACK_ZERO)]),
      ),
    ]),
  );
}

export function normalizePercentageMatrix(
  rawMatrix,
  metrics = WEEKLY_RECAP_REFERENCE_METRICS,
  jDays = WEEKLY_RECAP_J_DAYS,
) {
  const fallbackMatrix = createDefaultPercentageMatrix(metrics, jDays);
  if (!rawMatrix || typeof rawMatrix !== "object") return fallbackMatrix;

  return Object.fromEntries(
    metrics.map((metric) => {
      const metricRaw = rawMatrix?.[metric.key];
      const normalizedByDay = Object.fromEntries(
        jDays.map((day) => {
          const offset = jDayLabelToOffset(day);
          const legacyLabel = Number.isFinite(offset) ? offsetToLegacyJDayLabel(offset) : null;

          let rawValue = metricRaw?.[day];
          if ((rawValue === undefined || rawValue === null) && legacyLabel) {
            rawValue = metricRaw?.[legacyLabel];
          }

          return [
            day,
            toFiniteNumber(rawValue, fallbackMatrix?.[metric.key]?.[day] ?? FALLBACK_ZERO),
          ];
        }),
      );
      return [metric.key, normalizedByDay];
    }),
  );
}

export function formatMetricValue(metric, value) {
  if (value === null || value === undefined || value === "") return "N/A";

  const num = Number(value);
  if (!Number.isFinite(num)) return "N/A";

  const decimals = Number.isInteger(metric?.decimals) ? metric.decimals : 0;
  const scalar = decimals > 0 ? num.toFixed(decimals) : String(Math.round(num));

  return metric?.unit ? `${scalar} ${metric.unit}` : scalar;
}

export function toNullableMetricNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export function computeActualVsPlannedPercent(planned, actual) {
  const p = toNullableMetricNumber(planned);
  const a = toNullableMetricNumber(actual);

  if (p === null || a === null) return null;
  if (p === 0) return null;

  return (a / p) * 100;
}

export function getPercentTargetTone(percent, isDarkMode) {
  const value = toNullableMetricNumber(percent);
  if (value === null) {
    return isDarkMode ? "text-gray-400" : "text-gray-500";
  }

  const deviation = Math.abs(value - 100);

  if (deviation <= 15) {
    return isDarkMode ? "text-emerald-300" : "text-emerald-700";
  }

  if (deviation <= 30) {
    return isDarkMode ? "text-amber-300" : "text-amber-700";
  }

  return isDarkMode ? "text-rose-300 font-bold" : "text-rose-700 font-bold";
}

export function getPercentTargetCellTone(percent, isDarkMode) {
  const value = toNullableMetricNumber(percent);
  if (value === null) return "";

  const deviation = Math.abs(value - 100);

  if (deviation <= 15) {
    return isDarkMode ? "bg-emerald-950/30" : "bg-emerald-50/80";
  }

  if (deviation <= 30) {
    return isDarkMode ? "bg-amber-950/25" : "bg-amber-50/80";
  }

  return isDarkMode ? "bg-rose-950/35" : "bg-rose-50/90";
}

export function formatPercentValue(value, decimals = 0) {
  const num = toNullableMetricNumber(value);
  if (num === null) return "N/A";
  return `${num.toFixed(decimals)}%`;
}

// ─── Day label overrides ───────────────────────────────────────────────────

const ALL_POSSIBLE_DAYS = new Set(
  Array.from({ length: 15 }, (_, i) => offsetToJDayLabel(i - 7)),
);

function sanitizeDayLabel(value) {
  if (typeof value !== "string") return "";
  const trimmed = value.trim();
  return trimmed.slice(0, 16);
}

export function normalizeDayLabelOverrides(rawOverrides) {
  const source =
    rawOverrides && typeof rawOverrides === "object" && !Array.isArray(rawOverrides)
      ? rawOverrides
      : {};

  const normalized = {};
  Object.entries(source).forEach(([day, rawLabel]) => {
    if (!ALL_POSSIBLE_DAYS.has(day)) return;
    const sanitized = sanitizeDayLabel(rawLabel);
    if (!sanitized) return;
    normalized[day] = sanitized;
  });
  return normalized;
}

export function getDayDisplayLabel(day, dayLabelOverrides = {}) {
  const override = dayLabelOverrides?.[day];
  if (typeof override === "string" && override.trim()) return override.trim();
  return day;
}

// Peak-speed metrics (Vmax / sprint max) — compared to reference max, not planned load.

export const WEEKLY_RECAP_VMAX_METRIC = Object.freeze({
  key: "vmax",
  labelKey: "weekly_recap.metrics.vmax",
  label: "Vmax",
  unit: "km/h",
  decimals: 1,
});

export const WEEKLY_RECAP_VMA_MAX_ROW = WEEKLY_RECAP_VMAX_METRIC;

export const WEEKLY_RECAP_MATCH_DAY_LABEL = "MD";

function dayHasAnyMetricValue(actualValues, metrics, day) {
  return (metrics || []).some((metric) => {
    const value = actualValues?.[metric?.key]?.[day];
    return toNullableMetricNumber(value) !== null;
  });
}

/**
 * Resolves which MD+N columns to show in recap tables.
 * - Excludes MD (match objective — not part of load comparison).
 * - Trims leading/trailing days where every displayed metric is N/A.
 * - Keeps middle gaps when N/A sits between two days with values.
 */
export function resolveWeeklyRecapVisibleDays({
  jDays,
  actualValues,
  metrics,
  matchDayLabel = WEEKLY_RECAP_MATCH_DAY_LABEL,
}) {
  const daysWithoutMatch = (jDays || []).filter((day) => day !== matchDayLabel);

  let firstWithValue = -1;
  let lastWithValue = -1;

  daysWithoutMatch.forEach((day, index) => {
    if (!dayHasAnyMetricValue(actualValues, metrics, day)) return;
    if (firstWithValue === -1) firstWithValue = index;
    lastWithValue = index;
  });

  if (firstWithValue === -1) {
    return {
      visibleDays: daysWithoutMatch,
      weeklyPercentStartDay: null,
      weeklyPercentDays: [],
      trimmedLeadingDays: [],
      trimmedTrailingDays: [],
    };
  }

  const visibleDays = daysWithoutMatch.slice(firstWithValue, lastWithValue + 1);
  const weeklyPercentStartDay = visibleDays.find((day) =>
    dayHasAnyMetricValue(actualValues, metrics, day),
  ) || null;
  const weeklyPercentDays = weeklyPercentStartDay
    ? visibleDays.slice(visibleDays.indexOf(weeklyPercentStartDay))
    : visibleDays;

  return {
    visibleDays,
    weeklyPercentStartDay,
    weeklyPercentDays,
    trimmedLeadingDays: daysWithoutMatch.slice(0, firstWithValue),
    trimmedTrailingDays: daysWithoutMatch.slice(lastWithValue + 1),
  };
}

// ─── Weekly similarity colour helpers ────────────────────────────────────────

export function getWeeklySimilarityRingColor(value) {
  const num = toNullableMetricNumber(value);
  if (num === null) return "#64748b";
  if (num >= 80) return "#10b981"; // emerald
  if (num >= 60) return "#f59e0b"; // amber
  return "#ef4444"; // red
}

export function getWeeklySimilarityTone(value, isDarkMode) {
  const num = toNullableMetricNumber(value);
  if (num === null) {
    return isDarkMode ? "bg-gray-800/70 text-gray-300" : "bg-gray-100 text-gray-600";
  }
  if (num >= 80) return isDarkMode ? "bg-emerald-900/30 text-emerald-300" : "bg-emerald-100 text-emerald-700";
  if (num >= 60) return isDarkMode ? "bg-amber-900/30 text-amber-300" : "bg-amber-100 text-amber-700";
  return isDarkMode ? "bg-rose-900/30 text-rose-300" : "bg-rose-100 text-rose-700";
}
